import { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { queryView, useOpenWindow, useWindow, useWindowShell } from '@krp/client-runtime'
import { platformMyResources } from '@krp/platform-ui'
import {
  AQSI_WINDOWS,
  CATALOG_PAGE_SIZE,
  CatalogScreen,
  ProbeSearchPanel,
  FEEDBACK_SCOPES,
  aqsiCashiers,
  aqsiDevices,
  aqsiFiscalCheck,
  aqsiGoods,
  aqsiProbeRebuild,
  aqsiProbeSearch,
  aqsiProbeStatus,
  catalogIntentOf,
  createCatalogActions,
  fiscalCheckCacheKey,
  makeSelectCatalogVm,
  ridKey,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { AQSI_MINE } from './aqsiMine'

/** Список зависит от прав: окно, открытое заново, читает его заново, а не из кэша. */
const FRESH_RIGHTS = { refetchOnMountOrArgChange: true } as const
/** Пока индекс поиска строится, его состояние перечитывается: «Строится…» сменяется «Индекс по снимку…» без нажатий. */
const PROBE_POLL_MS = 3000

/** Binding окна «Каталог AQSI» (rid из окна). */
export function AqsiCatalogBinding() {
  const window = useWindow()
  const shell = useWindowShell()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const noResource = resourceId === ''
  const intent = useAppSelector((state: RootState) => catalogIntentOf(state.aqsi.catalog, resourceId))
  const args = useMemo(() => ({ resourceId }), [resourceId])
  const pageArgs = useMemo(
    () => ({ resourceId, page: intent.page, pageSize: CATALOG_PAGE_SIZE }),
    [intent.page, resourceId],
  )
  platformMyResources.useQuerySubscription(AQSI_MINE.catalog, FRESH_RIGHTS)
  aqsiGoods.useQuerySubscription(noResource ? skipToken : pageArgs)
  aqsiDevices.useQuerySubscription(noResource ? skipToken : args)
  aqsiCashiers.useQuerySubscription(noResource ? skipToken : args)

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectCatalogVm()
    const selectSources = platformMyResources.select(AQSI_MINE.catalog)
    const selectGoods = aqsiGoods.select(pageArgs)
    const selectDevices = aqsiDevices.select(args)
    const selectCashiers = aqsiCashiers.select(args)
    const selectCheck = aqsiFiscalCheck.select({ fixedCacheKey: fiscalCheckCacheKey(resourceId), requestId: undefined })
    const feedbackKey = ridKey(resourceId, FEEDBACK_SCOPES.catalog)
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        sources: selectSources(state),
        goods: selectGoods(state),
        devices: selectDevices(state),
        cashiers: selectCashiers(state),
        check: selectCheck(state),
        intent: catalogIntentOf(state.aqsi.catalog, resourceId),
        feedback: state.aqsi.feedback.byKey[feedbackKey],
      })
  }, [args, pageArgs, resourceId])
  const vm = useAppSelector(selectVm)
  const actions = useMemo(
    () =>
      createCatalogActions(
        vm,
        intent,
        dispatch,
        () => window.proceedTo(shell.pathOf(AQSI_WINDOWS.catalogSource)),
        // Окно товара источника: правка карточки прямо в кабинете (модальное, через реестр окон).
        (goodId) => openWindow(AQSI_WINDOWS.good, { rid: resourceId, key: goodId }),
      ),
    [dispatch, intent, openWindow, resourceId, shell, vm, window],
  )
  // RP-SE12: блок «Поиск в AQSI» — отдельный индекс единой службы поиска по снимку кабинета. Строка и страница живут, пока открыто окно.
  const [probe, setProbe] = useState({ term: '', page: 1 })
  const statusResult = aqsiProbeStatus.useQuery(noResource ? skipToken : args, { pollingInterval: PROBE_POLL_MS, refetchOnMountOrArgChange: true })
  const searchResult = aqsiProbeSearch.useQuery(
    noResource || probe.term === '' ? skipToken : { resourceId, q: probe.term, page: probe.page },
  )
  const [rebuild, rebuilding] = aqsiProbeRebuild.useMutation()
  const statusView = useMemo(() => queryView(statusResult, () => false), [statusResult])
  const searchView = useMemo(() => queryView(searchResult, () => false), [searchResult])
  const probePanel = (
    <ProbeSearchPanel
      key={resourceId}
      status={statusResult.data ?? null}
      statusQuery={statusView}
      rebuilding={rebuilding.isLoading}
      onRebuild={() => void rebuild(args)}
      term={probe.term}
      page={probe.page}
      result={searchResult.data ?? null}
      searchQuery={searchView}
      onSearch={(term) => setProbe({ term, page: 1 })}
      onPage={(page) => setProbe((current) => ({ ...current, page }))}
      onOpenGood={(goodId) => openWindow(AQSI_WINDOWS.good, { rid: resourceId, key: goodId })}
    />
  )
  return <CatalogScreen vm={vm} actions={actions} probeSearch={probePanel} />
}
